import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CustomTable from "../../SharableInputs/Table";
import { useGetEverifyEmployees } from "../../hooks/everify/useGetEverifyEmployees";
import { useCase } from "../../context/CaseContext";
import {
  downloadFurtherActionNotice,
  downloadGenericResult,
  downloadReferralDate,
  fetchClosureReasons,
  submitCloseCase,
} from "../../lib/everifyStatusApi";

const PAGE_SIZE = 10;
const MAX_FETCH_SIZE = 20;
const ACTION_BEHAVIORS = {
  EMPLOYMENT_AUTHORIZED: { label: "Download Result", type: "download" },
  CLOSED: { label: "Download Result", type: "download" },
  UNCONFIRMED_DATA: { label: "Review & Edit Case", type: "navigate" },
  PHOTO_MATCH: { label: "Verify Photo", type: "navigate" },
  SCAN_AND_UPLOAD: { label: "Upload Document", type: "navigate" },
  PENDING_REFERRAL: { label: "Download FAN", type: "navigate" },
  REFERRED: { label: "Download Referral Letter", type: "navigate" },
  FINAL_NONCONFIRMATION: { label: "Close Case", type: "close" },
  CLOSE_CASE_AND_RESUBMIT: { label: "Close Case", type: "close" },
  MANUAL_REVIEW: { label: "No Action Required", type: "none" },
  QUEUED: { label: "No Action Required", type: "none" },
};

const RECEIPT_STATUS_MESSAGES = {
  EMPLOYMENT_AUTHORIZED:
    "This employee is authorized to work in the United States. No further action is required.",
  CLOSED: "This case has been closed.",
};

const STATUS_ORDER = [
  "DRAFT",
  "UNCONFIRMED_DATA",
  "EMPLOYMENT_AUTHORIZED",
  "PHOTO_MATCH",
  "SCAN_AND_UPLOAD",
  "MANUAL_REVIEW",
  "PENDING_REFERRAL",
  "REFERRED",
  "QUEUED",
  "FINAL_NONCONFIRMATION",
  "CLOSE_CASE_AND_RESUBMIT",
  "CLOSED",
];

const STATUS_CONFIG = {
  DRAFT: {
    label: "Draft",
    route: "/initiateeverify",
  },
  UNCONFIRMED_DATA: {
    label: "Data Mismatch",
    route: "/case/tentative-nc",
  },
  EMPLOYMENT_AUTHORIZED: {
    label: "Closed-Employment Authorized",
    route: null, //make a new page to show the message as in excel 
  },
  PHOTO_MATCH: {
    label: "Document Photo Verification Required",
    route: "/case/photo-verification",
  },
  SCAN_AND_UPLOAD: {
    label: "Document Upload Required",
    route: "/case/scan-upload",
  },
  MANUAL_REVIEW: {
    label: "Case under Manual Review",
    route: "/case/manual-review",
  },
  PENDING_REFERRAL: {
    label: "Tentative Non-Confirmation (Mismatch)",
    route: "/case/pending-referral",  //need some changes- add the first download FAN button, check the excel
  },
  REFERRED: {
    label: "Referred to SSA",
    route: "/case/referred", 
  },
  QUEUED: {
    label: "Queued",
    route: "/case/queued",
  },
  FINAL_NONCONFIRMATION: {
    label: "Final Nonconfirmation",
    route: "/case/final-nc",
  },
  CLOSE_CASE_AND_RESUBMIT: {
    label: "Technical Issue Occurred",
    route: "/case/technical-issue", 
  },
  CLOSED: {
    label: "Case Closed by User",
    route: null,
  },
};

const STATUS_DOWNLOADERS = {
  PENDING_REFERRAL: downloadFurtherActionNotice,
  REFERRED: downloadReferralDate,
};

const CLOSE_CASE_RESTRICTED_STATUSES = new Set([
  "MANUAL_REVIEW",
  "REFERRED",
  "QUEUED",
  "CLOSED",
]);

const mapStatusKey = (statusValue) => {
  if (typeof statusValue === "string") {
    return statusValue.toUpperCase();
  }
  const index = Number(statusValue);
  if (Number.isNaN(index)) return null;
  return STATUS_ORDER[index] || null;
};

const pickField = (source, keys) => {
  if (!source) return null;
  for (const key of keys) {
    const value = source[key];
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return null;
};

const formatDate = (value) => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatStatus = (statusKey) => {
  if (!statusKey) return "--";
  return STATUS_CONFIG[statusKey]?.label || "--";
};

const formatReceiptDate = () => {
  return new Date().toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const downloadBlobFile = (content, filename, mimeType = "application/octet-stream") => {
  const blob =
    content instanceof Blob ? content : new Blob([content], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const escapePdfText = (text = "") =>
  text.replace(/\\/g, "\\\\").replace(/\(/g, "\\(").replace(/\)/g, "\\)");

const wrapText = (text = "", maxChars = 68) => {
  const words = String(text).split(/\s+/);
  const lines = [];
  let current = "";
  words.forEach((word) => {
    const next = current.length ? `${current} ${word}` : word;
    if (next.length > maxChars) {
      if (current) {
        lines.push(current);
        current = word;
      } else {
        lines.push(next.slice(0, maxChars));
        current = next.slice(maxChars);
      }
    } else {
      current = next;
    }
  });
  if (current) {
    lines.push(current);
  }
  return lines.length ? lines : [""];
};

const buildReceiptPdfBlob = (employee) => {
  const statusKey = employee?.statusKey || "";
  const caseNumber = employee?.caseNumber || employee?.caseId || "Not Available";
  const employeeName =
    employee?.employeeName ||
    [employee?.firstName, employee?.lastName].filter(Boolean).join(" ").trim() ||
    "Employee";
  const statusLabel = employee?.statusLabel || statusKey || "Unknown";
  const today = formatReceiptDate();
  const mainLine =
    RECEIPT_STATUS_MESSAGES[statusKey] ||
    `Current case status: ${statusLabel}.`;
  const noteText = "";

  const PAGE_WIDTH = 612;
  const PAGE_HEIGHT = 792;
  const LAYOUT_TOP_Y = 770;
  const LAYOUT_BOTTOM_Y = 374;
  const LAYOUT_HEIGHT = LAYOUT_TOP_Y - LAYOUT_BOTTOM_Y;
  const MIN_TOP_PADDING = 96;
  const MIN_BOTTOM_PADDING = 72;

  const availableSpace = PAGE_HEIGHT - LAYOUT_HEIGHT;
  let topSpacing = Math.max(availableSpace / 2, MIN_TOP_PADDING);
  let bottomSpacing = availableSpace - topSpacing;
  if (bottomSpacing < MIN_BOTTOM_PADDING) {
    const adjustment = MIN_BOTTOM_PADDING - bottomSpacing;
    topSpacing = Math.max(topSpacing - adjustment, MIN_TOP_PADDING);
    bottomSpacing = availableSpace - topSpacing;
  }
  const verticalShift = (PAGE_HEIGHT - topSpacing) - LAYOUT_TOP_Y;
  const shiftY = (value) => Number((value + verticalShift).toFixed(2));

  const contentParts = [];

  const addFilledRect = (x, yTop, width, height, fillGray = 0.95) => {
    const top = shiftY(yTop);
    const y = top - height;
    contentParts.push(`${fillGray} g ${x} ${y} ${width} ${height} re f`);
    contentParts.push("0 g 0 G");
  };

  const addBorderRect = (x, yTop, width, height, lineWidth = 0.75) => {
    const top = shiftY(yTop);
    const yBottom = top - height;
    const xRight = x + width;
    contentParts.push(
      `${lineWidth} w ${x} ${top} m ${xRight} ${top} l ${xRight} ${yBottom} l ${x} ${yBottom} l h S`
    );
  };

  const addText = (font, size, x, y, text = "") => {
    const safe = escapePdfText(text);
    contentParts.push(
      `BT /${font} ${size} Tf 1 0 0 1 ${x.toFixed(2)} ${shiftY(y).toFixed(2)} Tm (${safe}) Tj ET`
    );
  };

  const addParagraph = (font, size, x, y, text, maxChars = 68, lineHeight = 16) => {
    const lines = wrapText(text, maxChars);
    lines.forEach((line, index) => {
      addText(font, size, x, y - index * lineHeight, line);
    });
  };

  const addLine = (x1, y1, x2, y2, width = 0.5) => {
    const startY = shiftY(y1);
    const endY = shiftY(y2);
    contentParts.push(`${width} w ${x1} ${startY} m ${x2} ${endY} l S`);
  };

  addText("F1", 26, 72, 770, "E-Verify Case Status");
  addText("F2", 12, 72, 748, `Generated on ${today}`);
  const badgeTop = 764;
  addFilledRect(360, badgeTop, 180, 32, 0.9);
  addBorderRect(360, badgeTop, 180, 32, 0.7);
  addText("F1", 11, 372, badgeTop - 12, "Current Status");
  addText("F2", 12, 372, badgeTop - 26, statusLabel);
  addLine(72, 730, 540, 730, 1.2);

  const detailRows = [
    { label: "Employee", value: employeeName },
    { label: "Case Number", value: caseNumber },
    { label: "Status", value: statusLabel },
  ];

  const tableWidth = 468;
  const detailRowHeight = 32;
  let detailY = 700;
  detailRows.forEach((row, index) => {
    const rowTop = detailY;
    const shade = index % 2 === 0 ? 0.985 : 0.955;
    addFilledRect(72, rowTop, tableWidth, detailRowHeight, shade);
    addBorderRect(72, rowTop, tableWidth, detailRowHeight, 0.25);
    addText("F1", 12, 84, rowTop - 14, row.label);
    addText("F2", 12, 220, rowTop - 14, row.value);
    detailY -= detailRowHeight;
  });

  const statusBoxTop = detailY - 16;
  const statusBoxHeight = 132;
  const statusBoxBottom = statusBoxTop - statusBoxHeight;
  addFilledRect(72, statusBoxTop, tableWidth, statusBoxHeight, 0.97);
  addBorderRect(72, statusBoxTop, tableWidth, statusBoxHeight, 0.9);
  addText("F1", 13, 92, statusBoxTop - 24, "Status Update");
  addParagraph("F2", 12, 92, statusBoxTop - 44, mainLine, 70, 16);

  const noteDividerY = statusBoxBottom - 16;
  addLine(72, noteDividerY, 540, noteDividerY, 0.4);
  addParagraph("F2", 11, 72, noteDividerY - 18, noteText, 90, 14);
  addText(
    "F2",
    10,
    72,
    noteDividerY - 66
  );

  const contentStream = contentParts.join("\n");

  const encoder = new TextEncoder();
  const contentBytes = encoder.encode(contentStream);

  const objects = [
    "<< /Type /Catalog /Pages 2 0 R >>",
    "<< /Type /Pages /Kids [3 0 R] /Count 1 >>",
    `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE_WIDTH} ${PAGE_HEIGHT}] /Contents 4 0 R /Resources << /Font << /F1 5 0 R /F2 6 0 R >> >> >>`,
    `<< /Length ${contentBytes.length} >>\nstream\n${contentStream}\nendstream`,
    "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>",
    "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>",
  ];

  const chunks = [];
  let offset = 0;
  const offsets = [0];
  const pushString = (str) => {
    const bytes = encoder.encode(str);
    chunks.push(bytes);
    offset += bytes.length;
  };

  pushString("%PDF-1.4\n");
  objects.forEach((body, index) => {
    offsets.push(offset);
    pushString(`${index + 1} 0 obj\n${body}\nendobj\n`);
  });

  const xrefStart = offset;
  let xref = `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  for (let i = 1; i < offsets.length; i += 1) {
    xref += `${String(offsets[i]).padStart(10, "0")} 00000 n \n`;
  }
  pushString(xref);

  const trailer = `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefStart}\n%%EOF`;
  pushString(trailer);

  return new Blob(chunks, { type: "application/pdf" });
};

const downloadResultReceipt = (employee) => {
  const caseNumber = employee?.caseNumber || employee?.caseId;
  const identifier = caseNumber || employee?.id || "case";
  const pdfBlob = buildReceiptPdfBlob(employee);
  downloadBlobFile(pdfBlob, `everify-${identifier}.pdf`, "application/pdf");
};

const STATUS_FILTER_OPTIONS = STATUS_ORDER.map((statusKey) => ({
  value: statusKey,
  label: formatStatus(statusKey),
})).filter((option) => option.label && option.label !== "--");

const SORT_COLUMN_OPTIONS = [
  { value: "Date", label: "Created Date" },
  { value: "FirstName", label: "First Name" },
  { value: "LastName", label: "Last Name" },
  { value: "Status", label: "Status" },
];

const SORT_ORDER_OPTIONS = [
  { value: "Desc", label: "Descending" },
  { value: "Asc", label: "Ascending" },
];


const normalizeEmployees = (employees = []) =>
  employees.map((row, index) => {
    const statusValue = pickField(row, ["status", "caseStatus", "case_status", "caseStatusDescription"]);
    const normalizedStatusKey =
      typeof statusValue === "string"
        ? statusValue.toUpperCase()
        : mapStatusKey(statusValue);
    const statusDisplay =
      normalizedStatusKey ||
      (statusValue !== undefined && statusValue !== null
        ? String(statusValue).toUpperCase()
        : "--");

    const firstName = pickField(row, ["firstName", "first_name"]) || "";
    const lastName = pickField(row, ["lastName", "last_name"]) || "";
    const createdOnRaw = pickField(row, [
      "date",
      "createdOn",
      "created_on",
      "createdDate",
      "created_date",
      "date_of_hire",
    ]);
    const createdOnDate = createdOnRaw ? new Date(createdOnRaw) : null;
    const createdOnTimestamp =
      createdOnDate && !Number.isNaN(createdOnDate.getTime())
        ? createdOnDate.getTime()
        : null;

    return {
      id:
        row?.everifyTransactionId ??
        row?.everify_transaction_id ??
        row?.id ??
        `employee-${index}`,
      employeeName:
        pickField(row, ["employeeName", "employee_name", "fullName", "full_name"]) ||
        [firstName, lastName].filter(Boolean).join(" ").trim() ||
        "--",
      firstName,
      lastName,
      caseId:
        pickField(row, [
          "everifyTransactionId",
          "everify_transaction_id",
          "caseId",
          "case_id",
        ]) || "--",
      caseNumber: pickField(row, ["caseNumber", "case_number", "everifyCaseNumber", "everify_case_number"]),
      ssn: pickField(row, ["ssnMasked", "ssn_masked", "ssn"]),
      statusKey: normalizedStatusKey,
      statusDisplay,
      statusLabel: formatStatus(normalizedStatusKey),
      statusRoute: STATUS_CONFIG[normalizedStatusKey]?.route || null,
      createdOn: formatDate(createdOnRaw),
      createdOnTimestamp,
      updatedOn: formatDate(
        pickField(row, ["updatedOn", "updated_on", "updatedDate", "updated_date"])
      ),
    };
  });

const EmptyState = ({ title, description, action }) => (
  <div className="home-grid-empty">
    <Typography variant="h6" component="p">
      {title}
    </Typography>
    {description && (
      <Typography variant="body2" component="p">
        {description}
      </Typography>
    )}
    {action}
  </div>
);

export default function EmployeesGrid() {
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [sortColumn, setSortColumn] = useState("Date");
  const [sortOrder, setSortOrder] = useState("Desc");
  const [showFilters, setShowFilters] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const navigate = useNavigate();
  const { setActiveCaseNumber } = useCase();
  const [downloadingRowId, setDownloadingRowId] = useState(null);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [closeDialogEmployee, setCloseDialogEmployee] = useState(null);
  const [closeReasons, setCloseReasons] = useState([]);
  const [closeReason, setCloseReason] = useState("");
  const [closeReasonsLoading, setCloseReasonsLoading] = useState(false);
  const [closeSubmitting, setCloseSubmitting] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(id);
  }, [searchTerm]);

  const query = useGetEverifyEmployees({ pageSize: MAX_FETCH_SIZE });
  useEffect(() => {
    if (!query.isFetching && !query.isLoading && !query.isError) {
      setLastRefreshed(new Date());
    }
  }, [query.data, query.isError, query.isFetching, query.isLoading]);

  const normalizedEmployees = useMemo(
    () => normalizeEmployees(query.data?.employees ?? []),
    [query.data]
  );

  const filteredEmployees = useMemo(() => {
    const searchValue = debouncedSearch.toLowerCase();
    const hasSearch = searchValue.length > 0;
    const statusSet = new Set(selectedStatuses);
    const hasStatusFilters = statusSet.size > 0;

    return normalizedEmployees.filter((employee) => {
      if (hasStatusFilters && (!employee.statusKey || !statusSet.has(employee.statusKey))) {
        return false;
      }
      if (hasSearch) {
        const haystack = [
          employee.employeeName,
          employee.caseNumber,
          employee.caseId,
          employee.statusLabel,
          employee.statusDisplay,
          employee.ssn,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(searchValue);
      }
      return true;
    });
  }, [normalizedEmployees, debouncedSearch, selectedStatuses]);

  const sortedEmployees = useMemo(() => {
    const rows = [...filteredEmployees];
    const collator = new Intl.Collator(undefined, { sensitivity: "base" });

    rows.sort((a, b) => {
      let comparison = 0;
      switch (sortColumn) {
        case "FirstName":
          comparison = collator.compare(a.firstName || "", b.firstName || "");
          break;
        case "LastName":
          comparison = collator.compare(a.lastName || "", b.lastName || "");
          break;
        case "Status":
          comparison = collator.compare(
            a.statusDisplay || a.statusLabel || "",
            b.statusDisplay || b.statusLabel || ""
          );
          break;
        case "Date":
        default:
          comparison =
            (a.createdOnTimestamp ?? 0) - (b.createdOnTimestamp ?? 0);
          break;
      }

      if (comparison === 0) {
        comparison = collator.compare(a.employeeName || "", b.employeeName || "");
      }

      return sortOrder === "Asc" ? comparison : -comparison;
    });

    return rows;
  }, [filteredEmployees, sortColumn, sortOrder]);

  const total = sortedEmployees.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const paginatedEmployees = useMemo(() => {
    const startIndex = (page - 1) * PAGE_SIZE;
    return sortedEmployees.slice(startIndex, startIndex + PAGE_SIZE);
  }, [sortedEmployees, page]);

  const refreshVisibleCaseStatuses = () => {
    query.refetch().finally(() => {
      setLastRefreshed(new Date());
    });
  };

  const showingFrom = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const showingTo = total === 0 ? 0 : Math.min(page * PAGE_SIZE, total);
  const canGoBack = page > 1;
  const canGoForward = page < totalPages;
  const filtersDirty =
    selectedStatuses.length > 0 || sortColumn !== "Date" || sortOrder !== "Desc";
  const searchApplied = debouncedSearch.length > 0;
  const hasActiveFilters = filtersDirty || searchApplied;
  const hasEmployees = normalizedEmployees.length > 0;
  const hasFilteredResults = sortedEmployees.length > 0;
  const formattedLastRefreshed = lastRefreshed
    ? lastRefreshed.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : "--";

  useEffect(() => {
    if (!query.isLoading && page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages, query.isLoading]);

  useEffect(() => {
    if (!closeDialogOpen || !closeDialogEmployee?.caseNumber) return undefined;
    let isActive = true;
    setCloseReasons([]);
    setCloseReason("");
    setCloseReasonsLoading(true);
    fetchClosureReasons(closeDialogEmployee.caseNumber)
      .then((reasons) => {
        if (isActive) {
          setCloseReasons(reasons);
        }
      })
      .catch(() => {
        if (isActive) {
          toast.error("Unable to load closure reasons.");
        }
      })
      .finally(() => {
        if (isActive) {
          setCloseReasonsLoading(false);
        }
      });
    return () => {
      isActive = false;
    };
  }, [closeDialogOpen, closeDialogEmployee?.caseNumber]);

  const handleStatusFilterChange = (event) => {
    const { value } = event.target;
    const nextValue = typeof value === "string" ? value.split(",") : value;
    setSelectedStatuses(nextValue);
    setPage(1);
  };

  const handleSortColumnChange = (event) => {
    setSortColumn(event.target.value);
    setPage(1);
  };

  const handleSortOrderChange = (event) => {
    setSortOrder(event.target.value);
    setPage(1);
  };

  const handleResetFilters = () => {
    setSelectedStatuses([]);
    setSortColumn("Date");
    setSortOrder("Desc");
    setPage(1);
  };

  const handleClearAllFilters = () => {
    setSearchTerm("");
    setDebouncedSearch("");
    handleResetFilters();
  };

  const goToFirstPage = () => {
    if (canGoBack) {
      setPage(1);
    }
  };

  const goToPreviousPage = () => {
    if (canGoBack) {
      setPage((prev) => Math.max(1, prev - 1));
    }
  };

  const goToNextPage = () => {
    if (canGoForward) {
      setPage((prev) => Math.min(totalPages, prev + 1));
    }
  };

  const goToLastPage = () => {
    if (canGoForward) {
      setPage(totalPages);
    }
  };

  const toggleFiltersVisibility = () => {
    setShowFilters(prev => !prev);
  };

  const handleExportExcel = () => {
    toast.info("Export functionality will be available soon.");
  };

  const navigateToStatus = (employee) => {
    if (!employee) return;
    const caseNumber = employee.caseNumber || employee.caseId;
    if (employee.statusRoute !== "/initiateeverify" && !caseNumber) {
      toast.error("Case number unavailable for this record.");
      return;
    }
    if (caseNumber) {
      setActiveCaseNumber(String(caseNumber));
    }
    if (employee.statusKey === "UNCONFIRMED_DATA") {
      navigate("/case/confirm-details");
      return;
    }
    if (employee.statusRoute) {
      navigate(employee.statusRoute);
    }
  };

  const handleDownloadResult = async (employee) => {
    if (!employee?.caseNumber) {
      toast.error("Case number unavailable for this record.");
      return;
    }

    const receiptRequired = Boolean(RECEIPT_STATUS_MESSAGES[employee.statusKey]);
    const downloader = STATUS_DOWNLOADERS[employee.statusKey] || downloadGenericResult;

    try {
      setDownloadingRowId(employee.id);
      if (receiptRequired) {
        downloadResultReceipt(employee);
      } else {
        await downloader(employee.caseNumber);
      }
    } catch (error) {
      toast.error("Unable to download the result.");
    } finally {
      setDownloadingRowId(null);
    }
  };



  const openCloseCaseDialog = (employee) => {
    if (!employee?.caseNumber) {
      toast.error("Case number unavailable for this record.");
      return;
    }
    setCloseDialogEmployee(employee);
    setCloseDialogOpen(true);
  };

  const resetCloseDialog = () => {
    setCloseDialogOpen(false);
    setCloseDialogEmployee(null);
    setCloseReasons([]);
    setCloseReason("");
    setCloseReasonsLoading(false);
    setCloseSubmitting(false);
  };

  const handleDialogClose = () => {
    if (closeSubmitting) return;
    resetCloseDialog();
  };

  const handleCloseCaseSubmit = async () => {
    if (!closeDialogEmployee?.caseNumber) {
      toast.error("Case number unavailable for this record.");
      return;
    }
    if (!closeReason) {
      toast.error("Please select a reason to close the case.");
      return;
    }
    try {
      setCloseSubmitting(true);
      await submitCloseCase(closeDialogEmployee.caseNumber, { case_closure_reason_code: closeReason });
      toast.success("Case closed successfully.");
      resetCloseDialog();
      query.refetch();
    } catch (error) {
      toast.error("Unable to close the case.");
      setCloseSubmitting(false);
    }
  };

  const tableColumns = useMemo(
    () => ({
      date: "Date",
      employee: "Employee",
      status: "Status",
      caseNumberCell: "Case Number",
      actions: "Actions",
    }),
    []
  );

  const baseActions = useMemo(() => [], []);

  const resolveActionDescriptor = (employee) => {
    const config = ACTION_BEHAVIORS[employee.statusKey] || null;
    if (!config) {
      return {
        label: "View Status",
        handler: () => navigateToStatus(employee),
        disabled: !employee.statusRoute,
        variant: "button",
      };
    }
    if (config.type === "download") {
      return {
        label: "Download Result",
        handler: () => handleDownloadResult(employee),
        disabled: !employee.caseNumber,
        variant: "button",
      };
    }
    if (config.type === "navigate") {
      return {
        label: config.label,
        handler: () => navigateToStatus(employee),
        disabled: !employee.statusRoute,
        variant: "button",
      };
    }
    if (config.type === "close") {
      return {
        label: "Close Case",
        handler: () => openCloseCaseDialog(employee),
        disabled: !employee.caseNumber,
        variant: "button",
      };
    }
    return {
      label: config.label,
      handler: null,
      disabled: true,
      variant: "text",
    };
  };

  const handleRowActionClick = descriptor => {
    if (!descriptor || descriptor.disabled || typeof descriptor.handler !== "function") {
      return;
    }
    descriptor.handler();
  };

  const tableRows = paginatedEmployees.map((employee) => {
    const statusTextClasses = ["status-text"];
    if (
      employee.statusKey === "FINAL_NONCONFIRMATION" ||
      employee.statusKey === "CLOSE_CASE_AND_RESUBMIT"
    ) {
      statusTextClasses.push("is-danger");
    } else if (employee.statusKey === "CLOSED") {
      statusTextClasses.push("is-muted");
    } else {
      statusTextClasses.push("is-warning");
    }

    const actionsList = [
      {
        action: employee.statusKey === "UNCONFIRMED_DATA" ? "Edit Case" : "View Status",
        actionClick: (_action, row) => navigateToStatus(row),
      },
      {
        action:
          downloadingRowId === employee.id ? "Downloading..." : "Download Result",
        actionClick: (_action, row) => handleDownloadResult(row),
      },
    ];
    if (!CLOSE_CASE_RESTRICTED_STATUSES.has(employee.statusKey)) {
      actionsList.push({
        action: "Close Case",
        actionClick: (_action, row) => openCloseCaseDialog(row),
      });
    }

    const actionDescriptor = resolveActionDescriptor(employee);
    return {
      ...employee,
      key: `everify-${employee.id}`,
      date: employee.createdOn || "--",
      employee: (
        <div className="home-grid-employee-cell">
          <div className="employee-name">{employee.employeeName}</div>
          {employee.ssn && <span className="employee-meta">{employee.ssn}</span>}
        </div>
      ),
      status: (
        <div className="status-cell">
          <span className={statusTextClasses.join(" ")}>
            {employee.statusDisplay || employee.statusLabel || "--"}
          </span>
        </div>
      ),
      statusAction: employee.statusRoute ? () => navigateToStatus(employee) : null,
      statusClass: employee.statusRoute ? "everify-status-cell" : "",
      caseNumberCell: employee.caseNumber ? (
        <span className="employee-meta">{employee.caseNumber}</span>
      ) : (
        "--"
      ),
      actions: (
        <button
          type="button"
          className={`home-grid-action-button${
            actionDescriptor.variant === "text" ? " no-action" : ""
          }`}
          onClick={() => handleRowActionClick(actionDescriptor)}
          disabled={actionDescriptor.disabled}
        >
          {actionDescriptor.label}
        </button>
      ),
      actionsList,
    };
  });

  return (
    <section className="home-grid-section">
      <Paper elevation={0} className="home-grid-card">
        <div className="home-grid-header home-grid-block">
          <Typography variant="h5" component="h2" className="home-grid-heading">
            Recent E-Verify Activity
          </Typography>
          <div className="home-grid-controls">
            <div className="home-grid-search-control">
              <SearchIcon fontSize="small" />
              <input
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search employees"
                aria-label="Search employees"
                autoComplete="off"
              />
            </div>
            <div className="home-grid-header-actions">
              <span className="home-grid-last-refreshed">
                Last refreshed: {formattedLastRefreshed}
              </span>
              <button
                type="button"
                className="home-grid-pill"
                onClick={refreshVisibleCaseStatuses}
                disabled={query.isFetching}
              >
                {query.isFetching ? (
                  <CircularProgress size={16} />
                ) : (
                  <RefreshIcon fontSize="small" />
                )}
                <span>Refresh</span>
              </button>
              <button
                type="button"
                className={`home-grid-pill ${showFilters ? "active" : ""}`}
                onClick={toggleFiltersVisibility}
                aria-pressed={showFilters}
              >
                <FilterListIcon fontSize="small" />
                <span>Filter</span>
              </button>
              <button
                type="button"
                className="home-grid-pill"
                onClick={handleExportExcel}
              >
                <FileDownloadOutlinedIcon fontSize="small" />
                <span>Export Excel</span>
              </button>
            </div>
          </div>
        </div>

        <div className={`home-grid-filters home-grid-block ${showFilters ? "is-visible" : ""}`}>
          <FormControl size="small" className="home-grid-filter">
            <InputLabel id="status-filter-label" shrink>
              Status
            </InputLabel>
            <Select
              labelId="status-filter-label"
              multiple
              value={selectedStatuses}
              onChange={handleStatusFilterChange}
              input={<OutlinedInput label="Status" />}
              displayEmpty
              renderValue={(selected) => {
                const values = Array.isArray(selected) ? selected : [];
                if (values.length === 0) {
                  return (
                    <span className="home-grid-filter-placeholder">All statuses</span>
                  );
                }
                return values
                  .map((value) => STATUS_CONFIG[value]?.label || value)
                  .join(", ");
              }}
              MenuProps={{ PaperProps: { style: { maxHeight: 360 } } }}
            >
              {STATUS_FILTER_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <div className="home-grid-status-option">
                    <Checkbox
                      checked={selectedStatuses.indexOf(option.value) > -1}
                      size="small"
                    />
                    <span>{option.label}</span>
                  </div>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            select
            size="small"
            label="Sort by"
            value={sortColumn}
            onChange={handleSortColumnChange}
            className="home-grid-filter"
          >
            {SORT_COLUMN_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label="Order"
            value={sortOrder}
            onChange={handleSortOrderChange}
            className="home-grid-filter home-grid-filter--narrow"
          >
            {SORT_ORDER_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <Button
            size="small"
            variant="outlined"
            onClick={handleResetFilters}
            disabled={!filtersDirty}
            className="home-grid-filter-reset"
          >
            Reset
          </Button>
        </div>

        <div className="home-grid-table home-grid-block">
          {query.isLoading ? (
            <div className="home-grid-loader">
              <CircularProgress size={32} />
              <Typography variant="body2" component="p">
                Loading E-Verify cases...
              </Typography>
            </div>
          ) : query.isError ? (
            <EmptyState
              title="We couldn't load your cases"
              description="Please try again or refresh the page."
              action={
                <button
                  type="button"
                  className="home-grid-retry"
                  onClick={() => query.refetch()}
                >
                  Retry
                </button>
              }
            />
          ) : !hasEmployees ? (
            <EmptyState
              title="No cases to show yet"
              description="Start a new E-Verify case and it will appear here instantly."
            />
          ) : !hasFilteredResults ? (
            <EmptyState
              title="No cases match your filters"
              description="Try adjusting the filters or search term."
              action={
                hasActiveFilters ? (
                  <button
                    type="button"
                    className="home-grid-retry"
                    onClick={handleClearAllFilters}
                  >
                    Clear filters
                  </button>
                ) : null
              }
            />
          ) : (
            <>
              <CustomTable
                name="everifyEmployees"
                columns={tableColumns}
                sortableColumns={[]}
                data={tableRows}
                order={sortOrder === "Asc" ? "asc" : "desc"}
                orderBy={sortColumn}
                handleSort={() => {}}
                showCount={false}
                hasActions={false}
                actionsList={baseActions}
                pagination={false}
              />

              <div className="home-grid-pagination home-grid-block">
                <Typography variant="body2" component="span">
                  {total > 0
                    ? `Showing ${showingFrom} to ${showingTo} of ${total} results`
                    : "Showing 0 results"}
                </Typography>
                <div className="home-grid-pagination-controls">
                  <button
                    type="button"
                    aria-label="First page"
                    className="home-grid-page-button"
                    onClick={goToFirstPage}
                    disabled={!canGoBack}
                  >
                    <KeyboardDoubleArrowLeftIcon fontSize="small" />
                  </button>
                  <button
                    type="button"
                    aria-label="Previous page"
                    className="home-grid-page-button"
                    onClick={goToPreviousPage}
                    disabled={!canGoBack}
                  >
                    <KeyboardArrowLeftIcon fontSize="small" />
                  </button>
                  <Typography variant="body2" component="span" className="home-grid-page-indicator">
                    {total > 0 ? `Page ${page} of ${totalPages}` : "Page 0 of 0"}
                  </Typography>
                  <button
                    type="button"
                    aria-label="Next page"
                    className="home-grid-page-button"
                    onClick={goToNextPage}
                    disabled={!canGoForward}
                  >
                    <KeyboardArrowRightIcon fontSize="small" />
                  </button>
                  <button
                    type="button"
                    aria-label="Last page"
                    className="home-grid-page-button"
                    onClick={goToLastPage}
                    disabled={!canGoForward}
                  >
                    <KeyboardDoubleArrowRightIcon fontSize="small" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </Paper>
      <Dialog open={closeDialogOpen} onClose={handleDialogClose} fullWidth maxWidth="sm">
        <DialogTitle>Close Case</DialogTitle>
        <DialogContent dividers>
          {closeDialogEmployee?.caseNumber && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              Case Number: <strong>{closeDialogEmployee.caseNumber}</strong>
            </Typography>
          )}
          {closeReasonsLoading ? (
            <div className="home-dialog-loader">
              <CircularProgress size={24} />
              <Typography variant="body2">Loading reasons...</Typography>
            </div>
          ) : closeReasons.length > 0 ? (
            <TextField
              select
              fullWidth
              label="Close Case Reason"
              value={closeReason}
              onChange={(event) => setCloseReason(event.target.value)}
            >
              <MenuItem value="">Select a reason</MenuItem>
              {closeReasons.map((reason) => (
                <MenuItem
                  key={reason.code || reason.value || reason}
                  value={reason.code || reason.value || reason}
                >
                  {reason.name || reason.label || reason.description || reason}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <Typography variant="body2">
              No closure reasons are available for this case.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} disabled={closeSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCloseCaseSubmit}
            disabled={
              closeSubmitting ||
              !closeReason ||
              closeReasonsLoading ||
              closeReasons.length === 0
            }
          >
            {closeSubmitting ? "Closing..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>
    </section>
  );
}
